import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Center, Group, Loader, Select, Stack, Switch, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiError, timeApi, type TimeCategory, type TimeCategoryInput, type TimeEntryInput, undoPaths } from '../../api';
import { TOTAL_LABEL, UNCATEGORIZED, UNCATEGORIZED_VALUE, formatDate, formatHours, formatPercent, parseGermanNumber, todayIso } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { Section } from '../../ui/Section';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';
import { usePageDownloads } from '../../export/downloads';
import { TimeFees } from './TimeFees';
import { categoryChoices, categoryOf } from './timeChoices';

const LAST_CATEGORY = 'pst.time.category.';
const remembered = (projectId: string): string | null => {
  try { return window.localStorage.getItem(LAST_CATEGORY + projectId); } catch { return null; }
};
const remember = (projectId: string, id: string): void => {
  try { window.localStorage.setItem(LAST_CATEGORY + projectId, id); } catch { /* private window */ }
};

const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Time recording, kept small: one line to book hours, the budget per category
 * as bars, and the entries below. A booking needs date and hours; the category
 * is chosen from a fixed list that ends with "Ohne Kategorie", so time can be
 * booked before any category exists. Task code and comment are optional. The
 * category last used is kept.
 */
export function TimePage(): JSX.Element {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canManageCategories = me?.permissions.includes('projectdata.write') ?? false;
  const sheet = useQuery({ queryKey: ['time', projectId], queryFn: () => timeApi.get(projectId) });
  usePageDownloads('time', [
    { key: 'csv', label: 'Zeiten als CSV', hint: 'Buchungen und Kategorien des Projekts', href: timeApi.csvUrl(projectId) },
    ...(sheet.data?.seesAll ? [{ key: 'fees', label: 'Honorarstand als CSV', hint: 'Honorar, Leistungsstand, abgerechnet, offen je Position – Grundlage für Abschläge', href: timeApi.feesUrl(projectId) }] : []),
  ]);
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['time', projectId] });
  const add = useMutation({ mutationFn: (body: TimeEntryInput) => timeApi.addEntry(projectId, body), onSettled: refresh });
  const undo = useUndo();
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & TimeEntryInput) => {
      const before = sheet.data?.entries.find((e) => e.id === id);
      const step = before
        ? guardedStep(`Zeiteintrag ${formatDate(before.date)}`, 'PATCH', undoPaths.timeEntry(projectId, id), inversePatch(body, before as unknown as Record<string, unknown>), body)
        : null;
      return undo.recordAfter(timeApi.updateEntry(projectId, id, body), step);
    },
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => timeApi.removeEntry(projectId, id), onSettled: refresh });
  const addCategory = useMutation({ mutationFn: (body: TimeCategoryInput) => timeApi.addCategory(projectId, body), onSuccess: () => setNewCategory(''), onSettled: refresh });
  const updateCategory = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & TimeCategoryInput) => {
      const before = sheet.data?.categories.find((c) => c.id === id);
      const step = before
        ? guardedStep(`Kategorie ${before.code}`, 'PATCH', undoPaths.timeCategory(projectId, id), inversePatch(body, before as unknown as Record<string, unknown>), body)
        : null;
      return undo.recordAfter(timeApi.updateCategory(projectId, id, body), step);
    },
    onSettled: refresh,
  });
  const removeCategory = useMutation({ mutationFn: (id: string) => timeApi.removeCategory(projectId, id), onSettled: refresh });
  const workStages = useMutation({ mutationFn: () => timeApi.workStages(projectId), onSettled: refresh });

  const [date, setDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [category, setCategory] = useState<string | null>(() => remembered(projectId));
  const [taskCode, setTaskCode] = useState('');
  const [comment, setComment] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const error = [add, update, remove, addCategory, updateCategory, removeCategory, workStages].map((m) => m.error).find(Boolean);

  if (sheet.isPending) return <Center h={200}><Loader /></Center>;
  if (sheet.isError) return <Alert color="red">Die Zeiterfassung konnte nicht geladen werden.</Alert>;
  const data = sheet.data;
  const categories = data.categories;
  const chosen = category === UNCATEGORIZED_VALUE || categories.some((c) => c.id === category) ? category! : (categories[0]?.id ?? UNCATEGORIZED_VALUE);
  const parsedHours = parseGermanNumber(hours);
  const valid = typeof parsedHours === 'number' && parsedHours > 0 && parsedHours <= 24 && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const byId = new Map(categories.map((c) => [c.id, c]));
  const entries = data.entries.filter((e) => !onlyMine || e.mine);
  const shown = showAll ? entries : entries.slice(0, 50);
  const budget = categories.reduce((s, c) => s + (c.budgetHours ?? 0), 0);
  const mineTotal = categories.reduce((s, c) => s + c.mine, 0) + data.uncategorized.mine;
  // The sum line: rest and use only where a budget is set, so hours without one do not count as overrun.
  const budgetedBooked = categories.filter((c) => c.budgetHours !== null).reduce((s, c) => s + c.booked, 0);
  const unpaidBooked = categories.filter((c) => c.unpaid).reduce((s, c) => s + c.booked, 0);

  const submit = (): void => {
    if (!valid) return;
    remember(projectId, chosen);
    add.mutate(
      { categoryId: categoryOf(chosen), date, hours: parsedHours, taskCode: taskCode.trim() || null, comment: comment.trim() || null },
      { onSuccess: () => { setHours(''); setComment(''); setTaskCode(''); } },
    );
  };

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        {error && (
          <Alert color="red" withCloseButton onClose={() => [add, update, remove, addCategory, updateCategory, removeCategory, workStages].forEach((m) => m.reset())}>
            {error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}
          </Alert>
        )}

        <Section title="Zeit erfassen" description="Datum, Stunden, worauf – der Rest ist freiwillig. Enter speichert.">
          <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <Group gap="xs" align="flex-end" wrap="wrap">
              <TextInput size="sm" type="date" label="Datum" value={date} onChange={(e) => setDate(e.currentTarget.value)} w={150} />
              <TextInput size="sm" label="Stunden" placeholder="z. B. 2,5" value={hours} onChange={(e) => setHours(e.currentTarget.value)} w={90} error={hours && !valid && parsedHours !== null ? ' ' : undefined} data-autofocus />
              <Select
                size="sm" label="Kategorie" w={380} allowDeselect={false}
                value={chosen}
                onChange={(v) => v && setCategory(v)}
                data={categoryChoices(categories)}
              />
              <TextInput size="sm" label="Vorgang" placeholder="z. B. 4.11" value={taskCode} onChange={(e) => setTaskCode(e.currentTarget.value)} w={100} />
              <TextInput size="sm" label="Kommentar" placeholder="was, kurz" value={comment} onChange={(e) => setComment(e.currentTarget.value)} style={{ flex: 1, minWidth: 200 }} />
              <Button type="submit" disabled={!valid} loading={add.isPending}>Erfassen</Button>
            </Group>
          </form>
        </Section>

        <Section
          title="Budget je Kategorie"
          description={`${formatHours(data.total)} erfasst${budget ? ` von ${formatHours(budget)} Budget` : ''}${data.seesAll ? '' : `, davon ${formatHours(mineTotal)} von mir`}. Unbezahlte Kategorien (Vorleistung, nicht beauftragte Extras) sind gekennzeichnet.`}
        >
          {categories.length === 0 && (
            <Text size="sm" c="dimmed">
              Noch keine Kategorien – bis dahin wird „{UNCATEGORIZED}“ gebucht.
              {canManageCategories ? ' Leistungsphasen oder eigene Kategorien, z. B. die Positionen des Angebots, unten anlegen.' : ' Kategorien legt die Projektleitung an.'}
            </Text>
          )}
          {(categories.length > 0 || data.uncategorized.booked > 0) && (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={3} className="pst-tabular">
              <Table.Thead>
                <Table.Tr>
                  {canManageCategories && <Table.Th w={30} />}
                  <Table.Th w={110}>Kategorie</Table.Th>
                  <Table.Th>Bezeichnung</Table.Th>
                  <Table.Th w={100} ta="right">Budget (h)</Table.Th>
                  <Table.Th w={100} ta="right">erfasst</Table.Th>
                  <Table.Th w={100} ta="right">Rest</Table.Th>
                  <Table.Th w={220}>Verbrauch</Table.Th>
                  <Table.Th w={100}>bezahlt</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {categories.map((c) => (
                  <Table.Tr key={c.id}>
                    {canManageCategories && (
                      <Table.Td>
                        <ActionIcon size="xs" variant="subtle" color="gray" aria-label={`Kategorie ${c.code} löschen`} title={c.booked > 0 ? 'Nur leere Kategorien lassen sich löschen' : 'Kategorie löschen'} disabled={c.booked > 0} onClick={() => removeCategory.mutate(c.id)}>✕</ActionIcon>
                      </Table.Td>
                    )}
                    <Table.Td><EditableText value={c.code} bold monospace onCommit={(code) => canManageCategories && updateCategory.mutate({ id: c.id, code })} /></Table.Td>
                    <Table.Td><EditableText value={c.label ?? ''} allowEmpty placeholder=" " onCommit={(label) => canManageCategories && updateCategory.mutate({ id: c.id, label: label || null })} /></Table.Td>
                    <Table.Td><AmountCell value={c.budgetHours} quantity readOnly={!canManageCategories} placeholder="—" onCommit={(budgetHours) => updateCategory.mutate({ id: c.id, budgetHours: budgetHours === null ? null : Math.max(0, budgetHours) })} /></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={600}>{formatHours(c.booked)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" c={c.remaining !== null && c.remaining < 0 ? 'red.7' : 'dimmed'}>{c.remaining === null ? '' : formatHours(c.remaining)}</Text></Table.Td>
                    <Table.Td><BudgetBar category={c} /></Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs" checked={!c.unpaid} disabled={!canManageCategories}
                        label={c.unpaid ? 'unbezahlt' : 'ja'}
                        onChange={(e) => updateCategory.mutate({ id: c.id, unpaid: !e.currentTarget.checked })}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
                {data.uncategorized.booked > 0 && (
                  <Table.Tr>
                    {canManageCategories && <Table.Td />}
                    <Table.Td colSpan={2}><Text size="sm" fs="italic" c="dimmed">{UNCATEGORIZED}</Text></Table.Td>
                    <Table.Td />
                    <Table.Td ta="right"><Text size="sm" fw={600}>{formatHours(data.uncategorized.booked)}</Text></Table.Td>
                    <Table.Td colSpan={3} />
                  </Table.Tr>
                )}
                <Table.Tr className="pst-total-row">
                  {canManageCategories && <Table.Td />}
                  <Table.Td colSpan={2}><Text size="sm" fw={700}>{TOTAL_LABEL}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={700}>{budget ? formatHours(round2(budget)) : '—'}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(data.total)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={700} c={budgetedBooked > budget ? 'red.7' : 'dimmed'}>{budget ? formatHours(round2(budget - budgetedBooked)) : ''}</Text></Table.Td>
                  <Table.Td>{budget > 0 && <BudgetBar category={{ booked: round2(budgetedBooked), budgetHours: round2(budget), share: budgetedBooked / budget }} />}</Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{unpaidBooked > 0 ? `${formatHours(round2(unpaidBooked))} unbezahlt` : ''}</Text></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          )}
          {canManageCategories && (
            <form onSubmit={(e) => { e.preventDefault(); if (newCategory.trim()) addCategory.mutate({ code: newCategory.trim() }); }}>
              <Group gap="xs" align="flex-end">
                <TextInput size="xs" placeholder="neue Kategorie, z. B. Pos. 3 oder E11" value={newCategory} onChange={(e) => setNewCategory(e.currentTarget.value)} w={260} aria-label="Neue Kategorie" />
                <Button size="xs" type="submit" variant="light" disabled={!newCategory.trim()} loading={addCategory.isPending}>Kategorie hinzufügen</Button>
                {!categories.some((c) => c.code === 'LPH 1') && <Button size="xs" variant="subtle" onClick={() => workStages.mutate()}>Leistungsphasen 1–9 ergänzen</Button>}
              </Group>
            </form>
          )}
        </Section>

        {categories.length > 0 && data.seesAll && canManageCategories && (
          <TimeFees categories={categories} onChange={(id, body) => updateCategory.mutate({ id, ...body })} />
        )}

        <Section
          title="Einträge"
          description={data.seesAll ? 'Alle Einträge des Projekts.' : 'Deine Einträge. Die Einträge anderer sieht nur die Projektleitung; die Summen oben enthalten alle.'}
          actions={data.seesAll ? <Switch size="xs" label="nur meine" checked={onlyMine} onChange={(e) => setOnlyMine(e.currentTarget.checked)} /> : undefined}
        >
          {entries.length === 0 ? (
            <Text size="sm" c="dimmed">Noch keine Einträge.</Text>
          ) : (
            <Table verticalSpacing={2} className="pst-tabular" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={30} />
                  <Table.Th w={110}>Datum</Table.Th>
                  {data.seesAll && <Table.Th w={170}>Person</Table.Th>}
                  <Table.Th w={180}>Kategorie</Table.Th>
                  <Table.Th w={90}>Vorgang</Table.Th>
                  <Table.Th w={90} ta="right">Stunden</Table.Th>
                  <Table.Th>Kommentar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.map((e) => {
                  const cat = e.categoryId === null ? undefined : byId.get(e.categoryId);
                  const editable = e.mine || data.seesAll;
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>{editable && <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Eintrag löschen" title="Eintrag löschen" onClick={() => remove.mutate(e.id)}>✕</ActionIcon>}</Table.Td>
                      <Table.Td>
                        <EditableText value={formatDate(e.date)} onCommit={(v) => {
                          const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(v.trim());
                          if (editable && m) update.mutate({ id: e.id, date: `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}` });
                        }} />
                      </Table.Td>
                      {data.seesAll && <Table.Td><Text size="sm" c={e.mine ? undefined : 'dimmed'}>{e.person}</Text></Table.Td>}
                      <Table.Td>
                        <Select
                          size="xs" variant="unstyled" allowDeselect={false} disabled={!editable} value={e.categoryId ?? UNCATEGORIZED_VALUE}
                          onChange={(v) => v && categoryOf(v) !== e.categoryId && update.mutate({ id: e.id, categoryId: categoryOf(v) })}
                          data={categoryChoices(categories, false)}
                          rightSection={cat?.unpaid ? <Badge size="xs" color="gray" variant="light">unbez.</Badge> : undefined}
                          rightSectionWidth={cat?.unpaid ? 50 : undefined}
                        />
                      </Table.Td>
                      <Table.Td><EditableText value={e.taskCode ?? ''} monospace allowEmpty placeholder=" " onCommit={(taskCode) => editable && update.mutate({ id: e.id, taskCode: taskCode || null })} /></Table.Td>
                      <Table.Td><AmountCell value={e.hours} quantity readOnly={!editable} onCommit={(h) => h !== null && h > 0 && h <= 24 && update.mutate({ id: e.id, hours: h })} /></Table.Td>
                      <Table.Td><EditableText value={e.comment ?? ''} allowEmpty placeholder=" " onCommit={(comment) => editable && update.mutate({ id: e.id, comment: comment || null })} /></Table.Td>
                    </Table.Tr>
                  );
                })}
                <Table.Tr className="pst-total-row">
                  <Table.Td />
                  <Table.Td colSpan={data.seesAll ? 4 : 3}>
                    <Text size="sm" fw={700}>
                      {TOTAL_LABEL}
                      <Text span size="xs" c="dimmed" fw={400}> · {entries.length === 1 ? '1 Eintrag' : `${entries.length} Einträge`}{onlyMine ? ', nur meine' : ''}</Text>
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={700}>{formatHours(round2(entries.reduce((t, e) => t + e.hours, 0)))}</Text></Table.Td>
                  <Table.Td />
                </Table.Tr>
              </Table.Tbody>
            </Table>
          )}
          {entries.length > shown.length && <Button size="xs" variant="subtle" onClick={() => setShowAll(true)}>alle {entries.length} Einträge zeigen</Button>}
        </Section>
      </Stack>
    </div>
  );
}

/** Booked against budget as one bar; past the budget it turns red and says by how much. */
function BudgetBar({ category }: { category: Pick<TimeCategory, 'booked' | 'budgetHours' | 'share'> }): JSX.Element | null {
  if (category.share === null) return <Text size="xs" c="dimmed">{category.booked > 0 ? 'ohne Budget' : ''}</Text>;
  const over = category.share > 1;
  return (
    <Tooltip label={`${formatHours(category.booked)} von ${formatHours(category.budgetHours)}`}>
      <Group gap={6} wrap="nowrap">
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-default-border)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(category.share, 1) * 100}%`, height: '100%', borderRadius: 4, background: over ? 'var(--mantine-color-red-6)' : category.share > 0.8 ? 'var(--mantine-color-orange-5)' : 'var(--mantine-color-brand-6)' }} />
        </div>
        <Text size="xs" c={over ? 'red.7' : 'dimmed'} fw={over ? 600 : undefined} style={{ whiteSpace: 'nowrap' }}>
          {over ? `+${formatPercent((category.share - 1) * 100)} über` : formatPercent(category.share * 100)}
        </Text>
      </Group>
    </Tooltip>
  );
}
